/**
 * AdminStatsPanel — Grid of platform stat cards for admin dashboard.
 *
 * Shows: tokens launched, total volume, active wallets (24h),
 * and verification breakdown (verified/flagged/unverified).
 *
 * Story 7.3 — Task 3
 */

import type { AdminStatsResponse } from "@repo/shared-types";

import { formatCompact, formatCount } from "../../lib/format-number";

type AdminStatsPanelProps = {
	stats: AdminStatsResponse | undefined;
	isLoading: boolean;
};

function StatCard({
	label,
	value,
	color,
}: {
	label: string;
	value: string;
	color?: string;
}) {
	return (
		<div className="rounded-lg border border-pastel-border bg-pastel-white p-4">
			<p className="text-sm text-pastel-gray">{label}</p>
			<p className={`mt-1 text-2xl font-bold ${color ?? "text-pastel-black"}`}>{value}</p>
		</div>
	);
}

function SkeletonCard() {
	return (
		<div className="rounded-lg border border-pastel-border bg-pastel-white p-4">
			<div className="h-4 w-24 animate-pulse rounded bg-pastel-light-gray" />
			<div className="mt-2 h-8 w-16 animate-pulse rounded bg-pastel-light-gray" />
		</div>
	);
}

export function AdminStatsPanel({ stats, isLoading }: AdminStatsPanelProps) {
	if (isLoading) {
		return (
			<div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
				{Array.from({ length: 6 }).map((_, i) => (
					<SkeletonCard key={`skeleton-${String(i)}`} />
				))}
			</div>
		);
	}

	if (!stats) return null;

	const volume = stats.totalVolume !== null ? formatCompact(Number(stats.totalVolume)) : "N/A";
	const wallets = stats.activeWallets24h !== null ? formatCount(stats.activeWallets24h) : "N/A";

	return (
		<div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
			<StatCard label="Tokens Launched" value={formatCount(stats.tokensLaunched)} />
			<StatCard label="Total Volume" value={volume} />
			<StatCard label="Active Wallets (24h)" value={wallets} />
			<StatCard
				label="Verified"
				value={formatCount(stats.verificationBreakdown.verified)}
				color="text-green-600"
			/>
			<StatCard
				label="Flagged"
				value={formatCount(stats.verificationBreakdown.flagged)}
				color="text-yellow-600"
			/>
			<StatCard
				label="Unverified"
				value={formatCount(stats.verificationBreakdown.unverified)}
				color="text-pastel-gray"
			/>
		</div>
	);
}
